import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";

export const useFetchSeason = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport, seasonId } = useParams();
  let URL = `${sport}/o/${organizationId}/seasons/${seasonId}`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["seasons", sport, organizationId, seasonId, JSON.stringify(params)],
    queryFn: async () => {
      return await fetchData({
        url: URL,
      });
    },
  });
};

export const useCreateSeason = () => {
  const { sport, organizationId, competitionId } = useParams();
  const { mutateData } = useApi();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();

  return useMutation({
    mutationFn: async (data) => {
      return await mutateData({
        url: `${sport}/o/${organizationId}/competitions/${competitionId}/seasons`,
        method: "POST",
        data,
      }).then((response) => response?.data?.[0]);
    },
    onSuccess: async (createdSeason) => {
      queryClient.setQueryData(
        ["permission-scope-current-user", sport, organizationId],
        (oldScope) => {
          if (!oldScope) {
            return;
          }

          const updatedScope = JSON.parse(JSON.stringify(oldScope));
          if ("seasons" in updatedScope.resources) {
            updatedScope.resources.seasons[createdSeason.seasonId] = createdSeason;
          } else {
            updatedScope.resources.seasons = {
              [createdSeason.seasonId]: createdSeason,
            }
          }

          const leagueIdToUpdate = updatedScope.resources?.competitions?.[createdSeason.competitionId]?.leagueId || null;
          const leagueToUpdate = updatedScope.scope.leagueTree.find(({ leagueId }) => leagueId === leagueIdToUpdate);
          if (leagueToUpdate) {
            const competitionToUpdate = leagueToUpdate.competitions.find(({ competitionId }) => competitionId === createdSeason.competitionId);
            if (competitionToUpdate) {
              competitionToUpdate.seasonIds.push(createdSeason.seasonId);
            }
          }

          return updatedScope;
        }
      );

      notification.success({
        message: formatMessage({ id: "season.add.success" }),
        placement: "bottomRight",
      });

      await invalidateCache();
    },
  });
};

export const useUpdateSeason = () => {
  const { sport, organizationId, seasonId } = useParams();
  const queryClient = useQueryClient();
  const { mutateData } = useApi();
  const { formatMessage } = useIntl();
  const notification = useNotification();
  const { mutateAsync: invalidateCache } = useInvalidateCache();

  return useMutation({
    mutationFn: async (data) => {
      return await mutateData({
        url: `${sport}/o/${organizationId}/seasons/${seasonId}`,
        method: "PUT",
        data,
      });
    },
    onSuccess: async () => {
      notification.success({
        message: formatMessage({ id: "season.update.success" }),
        placement: "bottomRight",
      });

      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["seasons", sport, organizationId, seasonId] }),
      ]);
    },
  });
};